// FLAGS: personal-data
// Assigns experts to a session: renders their names, owns the licence gate and the 3-person cap.
import { useMemo, useState } from "react";
import { Trash2 } from "lucide-react";
import {
  ExpertRole,
  HostableExpert,
  SessionExpertInput,
  SpaceEventType,
} from "@src/models/spaces";
import RoleToggle from "@src/components/spaces/RoleToggle";
import { Input } from "@openup/shared/primitives/ui/input";
import {
  MAX_EXPERTS_PER_SESSION,
  secondaryRoleFor,
  setExpertRole,
} from "@src/components/spaces/createEventForm";

interface SessionExpertsProps {
  assigned: SessionExpertInput[];
  experts: HostableExpert[];
  eventType: SpaceEventType;
  error?: string;
  onChange: (next: SessionExpertInput[]) => void;
}

/** Expert assignment for one session: add experts and flag their role per event type. */
const SessionExperts = ({
  assigned,
  experts,
  eventType,
  error,
  onChange,
}: SessionExpertsProps) => {
  // A group session (meeting) has only elevated roles (host/co-host), both of
  // which must be a licensed Zoom user; a masterclass (webinar) lets unlicensed
  // experts join as panelists.
  const isGroupSession = eventType === "groupsession";
  const secondaryRole = secondaryRoleFor(eventType);
  const secondaryLabel = secondaryRole === "cohost" ? "co-host" : "panelist";

  const [query, setQuery] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);

  const assignedIds = new Set(assigned.map((e) => e.userId));
  const available = experts.filter((e) => !assignedIds.has(e.userId));
  const atCapacity = assigned.length >= MAX_EXPERTS_PER_SESSION;
  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    return term
      ? available.filter((e) => e.fullName.toLowerCase().includes(term))
      : available;
  }, [available, query]);
  const expertById = (userId: string) =>
    experts.find((e) => e.userId === userId);

  /** Whether an expert can be added at all — group sessions require a licence. */
  const canAdd = (expert: HostableExpert) => expert.licensed || !isGroupSession;

  const addExpert = (userId: string) => {
    const expert = expertById(userId);
    if (!expert || !canAdd(expert)) {
      return;
    }
    // Licensed experts default to host; unlicensed ones (masterclass only) take
    // the secondary role since they can't host.
    const role: ExpertRole = expert.licensed ? "host" : secondaryRole;
    onChange(setExpertRole(assigned, userId, role));
  };

  return (
    <div className="mt-5">
      <div className="mb-2 flex items-center gap-1 text-sm font-medium text-gray-800">
        Experts
        <span className="text-red-600" aria-hidden>
          *
        </span>
      </div>

      {assigned.length > 0 && (
        <ul className="mb-3 flex flex-col gap-2">
          {assigned.map((assignment) => {
            const expert = expertById(assignment.userId);
            if (!expert) {
              return null;
            }
            return (
              <li
                key={assignment.userId}
                className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2"
              >
                <span className="text-sm text-gray-800">
                  {expert.fullName}
                  {!expert.licensed && (
                    <span className="ml-2 text-xs text-amber-700">
                      no Zoom licence
                    </span>
                  )}
                </span>
                <div className="flex items-center gap-2">
                  <RoleToggle
                    role={assignment.role}
                    eventType={eventType}
                    hostDisabled={!expert.licensed}
                    onChange={(role) =>
                      onChange(setExpertRole(assigned, assignment.userId, role))
                    }
                  />
                  <button
                    type="button"
                    aria-label={`Remove ${expert.fullName}`}
                    className="text-gray-400 hover:text-red-600"
                    onClick={() =>
                      onChange(setExpertRole(assigned, assignment.userId, null))
                    }
                  >
                    <Trash2 className="h-4 w-4" aria-hidden />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <div
        className="relative"
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node)) {
            setPickerOpen(false);
          }
        }}
      >
        <Input
          type="text"
          role="combobox"
          aria-label="Add an expert"
          aria-expanded={pickerOpen}
          autoComplete="off"
          value={query}
          disabled={!available.length || atCapacity}
          placeholder={
            atCapacity
              ? `Session is full (max ${MAX_EXPERTS_PER_SESSION} people)`
              : available.length
                ? "Search experts by name…"
                : "All experts added"
          }
          onFocus={() => setPickerOpen(true)}
          onChange={(e) => {
            setQuery(e.target.value);
            setPickerOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setPickerOpen(false);
            }
          }}
        />
        {pickerOpen && !atCapacity && available.length > 0 && (
          <ul
            role="listbox"
            className="absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
          >
            {filtered.length === 0 ? (
              <li className="px-3 py-2 text-sm text-gray-500">
                No experts found.
              </li>
            ) : (
              filtered.map((expert) => (
                <li key={expert.userId} role="option" aria-selected={false}>
                  <button
                    type="button"
                    disabled={!canAdd(expert)}
                    className="flex w-full items-center justify-between px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-400 disabled:hover:bg-transparent"
                    onClick={() => {
                      addExpert(expert.userId);
                      setQuery("");
                      setPickerOpen(false);
                    }}
                  >
                    <span>{expert.fullName}</span>
                    {!expert.licensed && (
                      <span className="ml-2 text-xs text-amber-700">
                        {isGroupSession
                          ? "needs Zoom licence"
                          : "panelist only"}
                      </span>
                    )}
                  </button>
                </li>
              ))
            )}
          </ul>
        )}
      </div>

      <p className="mt-1.5 text-xs text-gray-500">
        Max {MAX_EXPERTS_PER_SESSION} people per session, host included. Hosts
        can start the Zoom session; {secondaryLabel}s
        {isGroupSession
          ? " help run it but can't start it."
          : " can present but can't start it."}
      </p>

      {error && (
        <p role="alert" className="mt-1.5 text-sm text-red-600">
          {error}
        </p>
      )}
    </div>
  );
};

export default SessionExperts;
